import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { Clock, Eye, X, Users } from "lucide-react";
import "./TableWaitingModal.css";

const HERO_IMAGE = "https://file.weltas.es/ai-studio/1779695011028_eaa49d5151d24f95.webp";

function minutesElapsed(from) {
  if (!from) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(from).getTime()) / 60000));
}

export default function TableWaitingModal({
  table,
  openRequest,
  onViewMenu,
  onCancel,
  isCancelling = false
}) {
  const [mins, setMins] = useState(() => minutesElapsed(openRequest?.requestedAt));

  useEffect(() => {
    const tick = () => setMins(minutesElapsed(openRequest?.requestedAt));
    tick();
    const id = setInterval(tick, 20000);
    return () => clearInterval(id);
  }, [openRequest?.requestedAt]);

  return (
    <motion.div
      className="tw-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        className="tw-modal"
        initial={{ scale: 0.94, y: 24, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.96, y: 12, opacity: 0 }}
        transition={{ type: "spring", damping: 22, stiffness: 320 }}
        role="dialog"
        aria-labelledby="tw-modal-title"
      >
        <motion.div
          className="tw-modal-hero"
          animate={{ rotate: [0, 1.5, -1.5, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        >
          <img src={HERO_IMAGE} alt="Esperando apertura" loading="eager" />
        </motion.div>

        <h2 id="tw-modal-title">Solicitud enviada</h2>
        <p>Estamos avisando al personal. La mesa se abrirá en breve.</p>

        <div className="tw-modal-info">
          <span className="tw-modal-info-row">
            <strong>{openRequest?.customerName || "—"}</strong>
          </span>
          <span className="tw-modal-info-row tw-modal-info-muted">
            <Users size={14} />
            {openRequest?.partySize || 0} {openRequest?.partySize === 1 ? "persona" : "personas"}
          </span>
          {openRequest?.customerNote && (
            <span className="tw-modal-info-note">{openRequest.customerNote}</span>
          )}
        </div>

        <motion.div
          className="tw-modal-counter"
          animate={{ opacity: [0.65, 1, 0.65] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <Clock size={16} />
          {mins === 0 ? (
            <span>Solicitada hace un momento</span>
          ) : (
            <span>Esperando hace {mins} {mins === 1 ? "min" : "min"}</span>
          )}
        </motion.div>

        <div className="tw-modal-dots" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              animate={{ y: [0, -8, 0], opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 1.1, delay: i * 0.18, repeat: Infinity, ease: "easeInOut" }}
            >
              •
            </motion.span>
          ))}
        </div>

        <small className="tw-modal-mesa">Mesa M{table?.number ?? "—"}</small>

        <div className="tw-modal-actions">
          <motion.button
            className="tw-modal-view"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={onViewMenu}
          >
            <Eye size={16} /> Ver carta mientras espero
          </motion.button>
          <motion.button
            className="tw-modal-cancel"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={onCancel}
            disabled={isCancelling}
          >
            <X size={14} /> {isCancelling ? "Cancelando…" : "Cancelar solicitud"}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
